import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid } from 'slices/commonSelectors';
import { useUscgFocRequirementsFlag } from 'utils/featureFlags';
import { USCG_FOC_SE_STORM_LAYER_NAME } from './uscgFocSoutheastTropicalData';
import { internalLayersKeys } from 'hooks/internalLayer/queryKeys';
import { layerNodesKeys } from 'hooks/layerNode/queryKeys';
import type {
  InternalLayer,
  ListInternalLayersFilter,
  ListLayersResponse,
} from 'hooks/internalLayer/types';
import { buildSearchQueryParams } from 'hooks/shared/buildSearchQueryParams';

const ensureLocks = new Map<string, Promise<unknown>>();

/** Location feature title seeded inside the FOC synthetic cone layer. */
export const USCG_FOC_SE_STORM_LOCATION_NAME =
  'Helene forecast cone (Southeast District)';

async function findFocSyntheticStormLayers(
  groupGuid: string
): Promise<InternalLayer[]> {
  const resp = await axios.get<ListLayersResponse<InternalLayer>>(
    config.apiGateway.internal_layers,
    {
      params: buildSearchQueryParams<ListInternalLayersFilter>({
        filter: { groupGuid, search: 'Helene forecast cone' },
        perPage: 50,
      }),
    }
  );
  return (resp.data?.data ?? []).filter(
    (layer) =>
      layer.name === USCG_FOC_SE_STORM_LAYER_NAME ||
      layer.name.includes('Helene forecast cone (Southeast District)')
  );
}

/**
 * FOC now reuses CFA Helene ERMA polygons. The earlier synthetic internal-layer
 * cone is redundant — archive any leftover copies so they stop painting on Hub.
 */
async function hideUscgFocSeStormLayersForGroup({
  groupGuid,
  queryClient,
}: {
  groupGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<number> {
  const layers = await findFocSyntheticStormLayers(groupGuid);
  if (layers.length === 0) return 0;

  await Promise.all(
    layers.map(async (layer) => {
      if (layer.defaultVisible !== false) {
        await axios.patch(`${config.apiGateway.internal_layers}/${layer.id}`, {
          defaultVisible: false,
        });
      }
      await axios.delete(`${config.apiGateway.internal_layers}/${layer.id}`);
    })
  );

  await queryClient.invalidateQueries({ queryKey: internalLayersKeys.all() });
  await queryClient.invalidateQueries({ queryKey: layerNodesKeys.all() });

  return layers.length;
}

/**
 * When FOC is on, remove the obsolete synthetic Helene cone internal layer
 * (ERMA polygons replace it).
 */
export function useEnsureUscgFocSeStormLayer() {
  const focEnabled = useUscgFocRequirementsFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!focEnabled || !groupGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    const lockKey = `uscg-foc-se-storm-layer-hide:${groupGuid}`;
    let cancelled = false;

    const run = async () => {
      const existing = ensureLocks.get(lockKey);
      if (existing) {
        await existing;
        if (!cancelled) completedRef.current = groupGuid;
        return;
      }

      const promise = hideUscgFocSeStormLayersForGroup({
        groupGuid,
        queryClient,
      })
        .catch((error) => {
          console.error(
            '[USCG FOC] Failed to hide obsolete storm cone layer',
            error
          );
          throw error;
        })
        .finally(() => {
          ensureLocks.delete(lockKey);
        });
      ensureLocks.set(lockKey, promise);

      try {
        await promise;
        if (!cancelled) completedRef.current = groupGuid;
      } catch {
        // Allow retry on next effect.
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [focEnabled, groupGuid, queryClient]);
}
